import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ACCESS_TOKEN, REFRESH_TOKEN } from '../constants';
import { UserAuth } from '../model/auth.model';
import { Router } from '@angular/router';
import { ResetPassword } from '../model/reset-password.model';

interface MockAuthData {
  access_token?: string;
  refresh_token?: string;
  user?: UserAuth;
}

interface GovBrCallbackResult {
  /** Ausente quando `perfil === 'prescritor'` — esse fluxo só devolve o handoff. */
  first_access?: boolean;
  perfil?: 'cidadao' | 'prescritor';
  handoff_code?: string;
  temp_token?: string;
  user_data?: GovBrUserData;
  auth_methods?: string[];
  reliability_level?: string;
  message?: string;
  access_token?: string;
  refresh_token?: string;
  user?: UserAuth;
}

interface GovBrUserData {
  cpf?: string;
  name?: string;
  email?: string;
  social_name?: string;
  phone_number?: string;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly apiUrl = `${environment.apiUrl}/citizen/auth/login`;
  private readonly profileUrl = `${environment.apiUrl}/citizen/profile`;
  private refreshUrl = `${environment.apiUrl}/citizen/auth/refresh`;
  private currentUserSubject: BehaviorSubject<UserAuth | null> = new BehaviorSubject<UserAuth | null>(null);
  public currentUser: Observable<UserAuth | null> = this.currentUserSubject.asObservable();
  private accessTokenKey = ACCESS_TOKEN;
  private refreshTokenKey = REFRESH_TOKEN;
  private refreshingToken = false;

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router
  ) {
    this.loadUser();
  }

  private loadUser() {
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      this.currentUserSubject = new BehaviorSubject<UserAuth | null>(JSON.parse(savedUser));
    } else {
      this.currentUserSubject = new BehaviorSubject<UserAuth | null>(null);
    }
    this.currentUser = this.currentUserSubject.asObservable();
  }

  private createAuthHeaders() {
    const accessToken = localStorage.getItem(this.accessTokenKey);
    if (accessToken) {
      return new HttpHeaders({
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      });
    }
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
  }

  private createBasicAuthHeaders() {
    const credentials = btoa('seshomolog:Montr3@l2025');

    return new HttpHeaders({
      Authorization: `Basic ${credentials}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
  }

  login(cpf: string, senha: string): Observable<UserAuth | null> {
    return this.http
      .post<{
        access_token: string;
        refresh_token: string;
      }>(`${this.apiUrl}`, { cpf: cpf, password: senha }, { headers: this.createAuthHeaders() })
      .pipe(
        switchMap((response) => {
          localStorage.setItem(this.accessTokenKey, response.access_token);

          return this.http.get<{ id: number; name: string; email: string; cpf: string; role: string }>(
            `${this.profileUrl}`,
            { headers: this.createAuthHeaders() }
          );
        }),
        map((profile: { id: number; name: string; email: string; cpf: string; role: string }) => {
          const user: UserAuth = {
            id: profile.id,
            name: profile.name,
            email: profile.email,
            cpf: profile.cpf,
            role: profile.role,
            auth_method: 'cpf_senha',
          };

          this.currentUserSubject.next(user);
          localStorage.setItem('currentUser', JSON.stringify(user));
          localStorage.setItem('auth_method', 'cpf_senha');

          return user;
        })
      );
  }

  refreshToken(): Observable<UserAuth | null> {
    if (this.refreshingToken) {
      return of(null);
    }

    const refreshToken = localStorage.getItem(this.refreshTokenKey);

    if (!refreshToken) {
      return of(null);
    }

    this.refreshingToken = true;

    return this.http
      .post<{
        accessToken: string;
      }>(this.refreshUrl, { refresh_token: refreshToken }, { headers: this.createAuthHeaders() })
      .pipe(
        switchMap((response) => {
          localStorage.setItem(this.accessTokenKey, response.accessToken);
          this.refreshingToken = false;
          return this.currentUser;
        }),
        catchError(() => {
          this.refreshingToken = false;
          this.clearToken();
          this.router.navigate(['/']);
          return of(null);
        }),
        finalize(() => {
          this.refreshingToken = false;
        })
      );
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem(this.accessTokenKey);
    return !!token;
  }

  getUserName(): string {
    return this.currentUserSubject.value?.name ?? '';
  }

  logout(): Observable<null> {
    const token = localStorage.getItem(this.accessTokenKey);
    if (token) {
      return this.http
        .post<null>(`${environment.apiUrl}/citizen/auth/logout`, {}, { headers: this.createAuthHeaders() })
        .pipe(
          finalize(() => {
            this.clearToken();
          }),
          catchError(() => {
            this.clearToken();
            return of(null);
          })
        );
    } else {
      this.clearToken();
      return of(null);
    }
  }

  resetPassword(data: ResetPassword): Observable<ResetPassword> {
    return this.http.post<ResetPassword>(`${environment.apiUrl}/citizen/auth/password/update`, data, {
      headers: this.createAuthHeaders(),
    });
  }

  initiateGovBrLogin(perfil: 'cidadao' | 'prescritor' = 'cidadao'): Observable<{ auth_url: string; state: string }> {
    return this.http
      .get<{
        auth_url: string;
        state: string;
      }>(`${environment.apiUrl}/citizen/auth/govbr/initiate?perfil=${perfil}`, { headers: this.createAuthHeaders() })
      .pipe(
        map((response) => {
          sessionStorage.setItem('govbr_state', response.state);
          return response;
        })
      );
  }

  /**
   * Cadastra um novo usuário de teste usando o register padrão e já faz o mock-login.
   * Gera name, email e password aleatórios a partir do CPF — sem endpoint extra no backend.
   */
  mockRegister(cpf: string): Observable<MockAuthData> {
    const cpfClean = cpf.replace(/\D/g, '');
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
    const plainPassword = Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');

    const payload = {
      name: `Mock ${cpfClean}`,
      cpf: cpfClean,
      mother_name: `Mãe ${cpfClean}`,
      birth_date: '1990-09-09',
      email: `mock_${cpfClean}@dev.local`,
      password: plainPassword,
      password_confirmation: plainPassword,
    };

    return this.http
      .post<MockAuthData>(`${environment.apiUrl}/citizen/auth/register`, payload, { headers: this.createAuthHeaders() })
      .pipe(
        switchMap(() => this.mockLogin(cpf)),
        catchError((error: HttpErrorResponse) => {
          console.error('🚨 MOCK REGISTER - Status:', error.status, '| Body:', error.error);
          throw error;
        })
      );
  }

  /**
   * Realiza o login de teste (mock)
   */
  mockLogin(cpf: string): Observable<MockAuthData> {
    return this.http
      .post<MockAuthData>(`${environment.apiUrl}/citizen/auth/mock-login`, { cpf }, { headers: this.createAuthHeaders() })
      .pipe(
        map((authData) => {
          if (authData.access_token) {
            localStorage.setItem(this.accessTokenKey, authData.access_token);
            localStorage.setItem('auth_method', 'cpf_senha');
            if (authData.user) {
              localStorage.setItem('currentUser', JSON.stringify(authData.user));
              this.currentUserSubject.next(authData.user);
            }
          }
          return authData;
        }),
        catchError((error: HttpErrorResponse) => {
          console.error('🚨 MOCK LOGIN - Erro:', error);
          throw error;
        })
      );
  }

  /**
   * Simula o login gov.br de um médico (sem passar pelo provedor real) — só
   * existe pra viabilizar o teste do handoff pro Portal do Prescritor em
   * dev/local/teste. Retorna o handoff_code já embutido na redirect_url que
   * o Portal do Prescritor consome.
   */
  mockLoginDoctor(cpf: string): Observable<{ handoff_code: string; redirect_url: string }> {
    return this.http
      .post<{
        handoff_code: string;
        redirect_url: string;
      }>(`${environment.apiUrl}/citizen/auth/mock-login-doctor`, { cpf }, { headers: this.createAuthHeaders() })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('🚨 MOCK LOGIN DOCTOR - Erro:', error);
          throw error;
        })
      );
  }

  /**
   * Processa o callback do Gov.br
   */
  handleGovBrCallback(code: string, state: string): Observable<GovBrCallbackResult> {
    // Validar state (proteção CSRF)
    const savedState = sessionStorage.getItem('govbr_state');

    if (!state || state !== savedState) {
      console.error('🚨 GOVBR CALLBACK - ERRO DE STATE:', {
        state_recebido: state,
        state_salvo: savedState,
        problema: !state ? 'State não fornecido' : 'States não coincidem',
      });
      throw new Error('State inválido - possível ataque CSRF');
    }

    return this.http
      .get<GovBrCallbackResult>(`${environment.apiUrl}/citizen/auth/govbr/callback?code=${code}&state=${state}`, {
        headers: this.createBasicAuthHeaders(),
      })
      .pipe(
        map((authData) => {
          // Se for primeiro acesso, salvar temp_token temporariamente
          if (authData.first_access === true && authData.temp_token) {
            sessionStorage.setItem('govbr_temp_token', authData.temp_token);
            console.log('💾 GOVBR CALLBACK - Temp token salvo para primeiro acesso');
          }

          // Se não for primeiro acesso, salvar tokens de acesso
          if (authData.first_access === false && authData.access_token) {
            localStorage.setItem(this.accessTokenKey, authData.access_token);
            localStorage.setItem('auth_method', 'govbr');
            if (authData.refresh_token) {
              localStorage.setItem(this.refreshTokenKey, authData.refresh_token);
            }
            if (authData.user) {
              const userWithAuth = { ...authData.user, auth_method: 'govbr' as const };
              localStorage.setItem('currentUser', JSON.stringify(userWithAuth));
              this.currentUserSubject.next(userWithAuth);
            }
          }

          // Limpar state
          sessionStorage.removeItem('govbr_state');

          return {
            first_access: authData.first_access,
            perfil: authData.perfil,
            handoff_code: authData.handoff_code,
            temp_token: authData.temp_token,
            user_data: authData.user_data,
            auth_methods: authData.auth_methods,
            reliability_level: authData.reliability_level,
            message: authData.message,
            access_token: authData.access_token,
            user: authData.user,
          };
        }),
        catchError((error: HttpErrorResponse) => {
          console.error('🚨 GOVBR CALLBACK - Erro HTTP:', {
            status: error.status,
            statusText: error.statusText,
            url: error.url,
            message: error.message,
          });

          // Tentar extrair detalhes do erro do corpo da resposta
          if (error.error) {
            console.error('🚨 GOVBR CALLBACK - Corpo do erro:', error.error);
          }

          throw error; // Re-throw para que o componente possa tratar
        })
      );
  }

  /**
   * Busca dados completos do usuário Gov.br
   */
  getGovBrUserData(): Observable<UserAuth> {
    return this.http.get<UserAuth>(`${environment.apiUrl}/citizen/profile`, { headers: this.createAuthHeaders() });
  }

  /**
   * Busca a foto do usuário Gov.br
   */
  getUserPicture(): Observable<{ picture_base64: string } | null> {
    return this.http
      .get<{
        picture_base64: string;
      }>(`${environment.apiUrl}/citizen/profile/picture`, { headers: this.createAuthHeaders() })
      .pipe(catchError(() => of(null)));
  }

  /**
   * Logout do Gov.br
   */
  logoutGovBr(): Observable<null> {
    const token = localStorage.getItem(this.accessTokenKey);
    if (token) {
      return this.http
        .post<null>(`${environment.apiUrl}/citizen/auth/logout`, {}, { headers: this.createAuthHeaders() })
        .pipe(
          finalize(() => {
            this.clearToken();
            window.location.href = `${environment.govbrSsoUrl}/logout?post_logout_redirect_uri=${environment.url}/auth/govbr/logout`;
          })
        );
    } else {
      this.clearToken();
      return of(null);
    }
  }

  /**
   * Limpa todos os tokens e dados de sessão
   */
  private clearToken() {
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('auth_method');
    sessionStorage.removeItem('govbr_state');
    this.currentUserSubject.next(null);
  }

  isGovBrAuth(): boolean {
    return localStorage.getItem('auth_method') === 'govbr';
  }

  /**
   * Atualiza o usuário atual no BehaviorSubject
   */
  updateCurrentUser(user: UserAuth): void {
    this.currentUserSubject.next(user);
  }
}
